import React, { useCallback, useEffect, useState } from 'react';
import { CalendarRange, Clock, CheckCircle2, Star, TrendingUp, TrendingDown, Minus, RefreshCw } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { easerGet, fmtMinutes, getStoredUser } from '../../lib/easerApi';
import { CloudOff } from 'lucide-react';

const DAY_LETTERS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

const fmtRange = (start, end) => {
  if (!start || !end) return '';
  const s = new Date(`${start}T12:00:00`); const e = new Date(`${end}T12:00:00`);
  const sameMonth = s.getMonth() === e.getMonth();
  const dayMonth = (d, withMonth) => d.toLocaleDateString('fr-FR', withMonth ? { day: 'numeric', month: 'short' } : { day: 'numeric' });
  return `${dayMonth(s, !sameMonth)} → ${dayMonth(e, true)}`;
};

/** Signed difference vs previous week (same weekdays), rendered as a small trend line. */
function Trend({ value, format = (v) => v, label = '', onDark = false, testId }) {
  if (value === undefined || value === null) return null;
  const Icon = value > 0 ? TrendingUp : value < 0 ? TrendingDown : Minus;
  const tone = onDark ? 'text-white/90' : value > 0 ? 'text-[hsl(var(--brand-sage))]' : value < 0 ? 'text-[hsl(var(--accent))]' : 'text-muted-foreground';
  return (
    <p className={`text-[11px] mt-1 flex items-center gap-1 ${tone}`} data-testid={testId}>
      <Icon className="h-3 w-3" /> {value > 0 ? '+' : ''}{format(value)} <span className="text-muted-foreground">{label}</span>
    </p>
  );
}

/** Accueil Easer: « Ma semaine » — heures réalisées, interventions et avis reçus (lundi → dimanche) vs semaine précédente. */
export function EaserWeekCard({ onUnauthorized }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [stale, setStale] = useState(false); // shown from the phone's last successful load (offline / Dolibarr down)
  const [busy, setBusy] = useState(false);
  const cacheKey = `easer_week_cache_${getStoredUser()?.id || 'me'}`;

  const load = useCallback(async (refresh = false) => {
    setBusy(true);
    try {
      const res = await easerGet('/api/easer/week', refresh ? { refresh: true } : {});
      setData(res.data);
      setError(false);
      setStale(false);
      try { localStorage.setItem(cacheKey, JSON.stringify({ at: Date.now(), data: res.data })); } catch { /* quota */ }
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      let cached = null;
      try { cached = JSON.parse(localStorage.getItem(cacheKey) || 'null'); } catch { cached = null; }
      if (cached?.data) {
        setData((d) => d || cached.data);
        setStale(true);
        setError(false);
      } else {
        setError(true);
      }
    } finally {
      setBusy(false);
    }
  }, [onUnauthorized, cacheKey]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onClock = () => load(true);
    window.addEventListener('easer:clock-changed', onClock);
    window.addEventListener('easer:dolibarr-restored', onClock);
    return () => { window.removeEventListener('easer:clock-changed', onClock); window.removeEventListener('easer:dolibarr-restored', onClock); };
  }, [load]);

  if (!data && !error) return <Skeleton className="h-44 rounded-xl" data-testid="easer-week-loading" />;

  if (!data) {
    return (
      <Card className="p-4 flex items-center justify-between gap-3" data-testid="easer-week-error">
        <p className="text-sm text-muted-foreground">Résumé de la semaine indisponible pour le moment (hors ligne ou Dolibarr en pause). Il reviendra automatiquement.</p>
        <Button size="sm" variant="outline" className="h-9" onClick={() => load(true)} disabled={busy}><RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} /></Button>
      </Card>
    );
  }

  const c = data.current; const d = data.delta || {};
  const todayIdx = Math.max(0, (new Date(`${data.today}T12:00:00`).getDay() + 6) % 7);
  const maxMin = Math.max(60, ...c.days.map((x) => Math.max(x.actual, x.planned)));
  const avg = c.reviews?.average;

  return (
    <Card className="p-4 shadow-sm border-border" data-testid="easer-week-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="h-10 w-10 rounded-xl bg-[hsl(var(--brand-mint))] text-[hsl(var(--primary))] flex items-center justify-center flex-shrink-0"><CalendarRange className="h-5 w-5" /></span>
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Ma semaine</p>
            <p className="text-sm font-medium truncate" data-testid="easer-week-range">{fmtRange(c.start, c.end)}</p>
          </div>
        </div>
        {stale && <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[hsl(var(--brand-apricot))] flex-shrink-0" data-testid="easer-week-stale"><CloudOff className="h-3 w-3" /> dernier chargement</span>}
        {!stale && data.remaining_count > 0 && <p className="text-xs text-muted-foreground text-right flex-shrink-0" data-testid="easer-week-remaining">{data.remaining_count} intervention{data.remaining_count > 1 ? 's' : ''}<br />encore prévue{data.remaining_count > 1 ? 's' : ''}</p>}
      </div>

      <div className="grid grid-cols-3 gap-2 mt-4">
        <div className="rounded-xl bg-[hsl(var(--primary))] text-white p-3" data-testid="easer-week-hours">
          <p className="text-[11px] font-medium text-white/80 flex items-center gap-1"><Clock className="h-3 w-3" /> Heures</p>
          <p className="text-xl font-semibold leading-tight mt-1 font-['Space_Grotesk']">{fmtMinutes(c.actual_minutes)}</p>
          <p className="text-[11px] text-white/80">sur {fmtMinutes(c.planned_minutes)} prévues</p>
          <Trend value={d.actual_minutes} format={(v) => fmtMinutes(Math.abs(v))} onDark testId="easer-week-hours-trend" />
        </div>
        <div className="rounded-xl border border-border bg-card p-3" data-testid="easer-week-interventions">
          <p className="text-[11px] font-medium text-muted-foreground flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Interventions</p>
          <p className="text-xl font-semibold leading-tight mt-1 font-['Space_Grotesk']">{c.done_count}<span className="text-sm text-muted-foreground font-normal"> / {c.planned_count}</span></p>
          <p className="text-[11px] text-muted-foreground">terminées</p>
          <Trend value={d.done_count} testId="easer-week-interventions-trend" />
        </div>
        <div className="rounded-xl border border-border bg-card p-3" data-testid="easer-week-reviews">
          <p className="text-[11px] font-medium text-muted-foreground flex items-center gap-1"><Star className="h-3 w-3" /> Avis reçus</p>
          <p className="text-xl font-semibold leading-tight mt-1 font-['Space_Grotesk']">{c.reviews?.count ?? 0}</p>
          <p className="text-[11px] text-muted-foreground">{avg ? `${avg}/5 en moyenne` : 'cette semaine'}</p>
          <Trend value={d.reviews_count} testId="easer-week-reviews-trend" />
        </div>
      </div>

      <div className="mt-4" aria-label="Heures par jour" data-testid="easer-week-days">
        <div className="grid grid-cols-7 gap-1.5 items-end h-14">
          {c.days.map((day, i) => {
            const hA = Math.round((day.actual / maxMin) * 100); const hP = Math.round((day.planned / maxMin) * 100);
            const isToday = i === todayIdx; const future = i > todayIdx;
            return (
              <div key={day.day} className="relative h-full flex items-end" title={`${day.day} : ${fmtMinutes(day.actual)} réalisées / ${fmtMinutes(day.planned)} prévues`}>
                {day.planned > 0 && <span className="absolute inset-x-0 bottom-0 rounded-md bg-[hsl(var(--primary))]/15" style={{ height: `${Math.max(hP, 6)}%` }} />}
                {day.actual > 0 && <span className={`absolute inset-x-0 bottom-0 rounded-md ${future ? 'bg-[hsl(var(--primary))]/40' : 'bg-[hsl(var(--primary))]'}`} style={{ height: `${Math.max(hA, 6)}%` }} />}
                {day.planned === 0 && day.actual === 0 && <span className="absolute inset-x-0 bottom-0 h-1 rounded-md bg-border" />}
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-7 gap-1.5 mt-1">
          {DAY_LETTERS.map((l, i) => (
            <span key={`${l}${i}`} className={`text-center text-[11px] ${i === todayIdx ? 'font-bold text-[hsl(var(--primary))]' : 'text-muted-foreground'}`}>{l}</span>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-3">
          <span className="inline-flex items-center gap-1"><span className="h-2 w-3 rounded-sm bg-[hsl(var(--primary))]" /> réalisé</span>
          <span className="inline-flex items-center gap-1"><span className="h-2 w-3 rounded-sm bg-[hsl(var(--primary))]/15" /> prévu</span>
          <span className="ml-auto text-right">écarts vs semaine dernière à ce jour · sem. dernière : {fmtMinutes(data.previous?.actual_minutes || 0)}, {data.previous?.done_count ?? 0} interv.</span>
        </p>
      </div>

      {c.reviews?.latest?.length > 0 && (
        <div className="mt-3 rounded-xl bg-[hsl(var(--secondary))]/60 p-3 text-sm" data-testid="easer-week-latest-review">
          <p className="flex items-center gap-1 text-[hsl(var(--accent))]">{Array.from({ length: c.reviews.latest[0].stars || 0 }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-current" />)}</p>
          {c.reviews.latest[0].comment && <p className="mt-1 italic text-foreground/90">« {c.reviews.latest[0].comment} »</p>}
          {c.reviews.latest[0].client_name && <p className="text-xs text-muted-foreground mt-0.5">{c.reviews.latest[0].client_name}</p>}
        </div>
      )}
    </Card>
  );
}
